/// <amd-module name="Artifacts/Cargo/services/CargoDataService" />
import * as VSS_CargoApi_Contracts from "Artifacts/Cargo/Contracts/CargoApi";
import { getCargoApiClient, ICargoApiRestClient } from "Artifacts/Cargo/RestClient/CargoApi";
import { BatchExecutor, IBatchIterator } from "Artifacts/Package/services/BatchHandling";
import { MinimalPackageDetails, UpstreamingBehavior } from "Artifacts/Root/types/PackagingShared";
import { IVssService, Services, VssService } from "VSS/Platform/Context";

export interface IFeedCargoDataService extends IVssService {
  readonly CargoApiRestClient: ICargoApiRestClient;
  batchSize: number;
  updatePackageVersions(
    cargoBatchRequest: VSS_CargoApi_Contracts.CargoPackagesBatchRequest,
    projectId: string,
    feedId: string,
  ): Promise<void>;
  batchDelete(
    projectId: string,
    feedId: string,
    iterator: IBatchIterator<MinimalPackageDetails[]>,
  ): Promise<void>;
  batchRestoreToFeed(
    projectId: string,
    feedId: string,
    iterator: IBatchIterator<MinimalPackageDetails[]>,
  ): Promise<void>;
  batchPermanentlyDelete(
    projectId: string,
    feedId: string,
    iterator: IBatchIterator<MinimalPackageDetails[]>,
  ): Promise<void>;
  batchPromote(
    projectId: string,
    feedId: string,
    viewId: string,
    iterator: IBatchIterator<MinimalPackageDetails[]>,
  ): Promise<void>;
  getUpstreamingBehavior(
    feedId: string,
    packageName: string,
    projectId?: string,
  ): Promise<UpstreamingBehavior>;
  setUpstreamingBehavior(
    feedId: string,
    packageName: string,
    behavior: UpstreamingBehavior,
    projectId?: string,
  ): Promise<void>;
}

class CargoDataService extends VssService implements IFeedCargoDataService {
  get CargoApiRestClient(): ICargoApiRestClient {
    return getCargoApiClient(this.pageContext);
  }

  get batchSize() {
    return 100; // SGV TODO
    // return this.pageContext
    // 	.getService<HubWebPageDataService>("IFeedWebPageDataService")
    // 	.getBatchOperationPageSize();
  }

  public updatePackageVersions(
    cargoBatchRequest: VSS_CargoApi_Contracts.CargoPackagesBatchRequest,
    projectId: string,
    feedId: string,
  ): Promise<void> {
    return this.CargoApiRestClient.updatePackageVersions(cargoBatchRequest, feedId, projectId);
  }

  public async batchDelete(
    projectId: string,
    feedId: string,
    iterator: IBatchIterator<MinimalPackageDetails[]>,
  ): Promise<void> {
    await BatchExecutor.executeInParallel(
      iterator,
      async (packageDetails: MinimalPackageDetails[]) => {
        const batchRequest = <VSS_CargoApi_Contracts.CargoPackagesBatchRequest>{
          data: null,
          operation: VSS_CargoApi_Contracts.CargoBatchOperationType.Delete,
          packages: packageDetails,
        };
        await this.CargoApiRestClient.updatePackageVersions(batchRequest, feedId, projectId);
      },
    );
  }

  public async batchRestoreToFeed(
    projectId: string,
    feedId: string,
    iterator: IBatchIterator<MinimalPackageDetails[]>,
  ): Promise<void> {
    await BatchExecutor.executeInParallel(
      iterator,
      async (packageDetails: MinimalPackageDetails[]) => {
        const batchRequest = <VSS_CargoApi_Contracts.CargoPackagesBatchRequest>{
          data: null,
          operation: VSS_CargoApi_Contracts.CargoBatchOperationType.RestoreToFeed,
          packages: packageDetails,
        };
        await this.CargoApiRestClient.updateRecycleBinPackageVersions(
          batchRequest,
          feedId,
          projectId,
        );
      },
    );
  }

  public async batchPermanentlyDelete(
    projectId: string,
    feedId: string,
    iterator: IBatchIterator<MinimalPackageDetails[]>,
  ): Promise<void> {
    await BatchExecutor.executeInParallel(
      iterator,
      async (packageDetails: MinimalPackageDetails[]) => {
        const batchRequest = <VSS_CargoApi_Contracts.CargoPackagesBatchRequest>{
          data: null,
          operation: VSS_CargoApi_Contracts.CargoBatchOperationType.PermanentDelete,
          packages: packageDetails,
        };
        await this.CargoApiRestClient.updateRecycleBinPackageVersions(
          batchRequest,
          feedId,
          projectId,
        );
      },
    );
  }

  public async batchPromote(
    projectId: string,
    feedId: string,
    viewId: string,
    iterator: IBatchIterator<MinimalPackageDetails[]>,
  ): Promise<void> {
    await BatchExecutor.executeInParallel(
      iterator,
      async (packageDetails: MinimalPackageDetails[]) => {
        const batchRequest = <VSS_CargoApi_Contracts.CargoPackagesBatchRequest>{
          data: { viewId },
          operation: VSS_CargoApi_Contracts.CargoBatchOperationType.Promote,
          packages: packageDetails,
        };
        await this.CargoApiRestClient.updatePackageVersions(batchRequest, feedId, projectId);
      },
    );
  }

  public getUpstreamingBehavior(
    feedId: string,
    packageName: string,
    projectId?: string,
  ): Promise<UpstreamingBehavior> {
    return this.CargoApiRestClient.getUpstreamingBehavior(feedId, packageName, projectId);
  }

  public setUpstreamingBehavior(
    feedId: string,
    packageName: string,
    behavior: UpstreamingBehavior,
    projectId: string,
  ): Promise<void> {
    return this.CargoApiRestClient.setUpstreamingBehavior(feedId, packageName, behavior, projectId);
  }
}

Services.add("IFeedCargoDataService", { serviceFactory: CargoDataService });
